import React, { useState, useEffect } from 'react';
import { Table, Card, Button, Space, Tag, Input, Modal, Form, InputNumber, message, Popconfirm, Switch } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, SearchOutlined, ReloadOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import api from '../../services/api';

interface RechargePackage {
  id: string;
  name: string;
  amount: number;
  giftAmount: number;
  totalAmount: number;
  validDays: number;
  description?: string;
  sort: number;
  isEnabled: number;
  createdAt: string;
}

const MemberRecharge: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<RechargePackage[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingRecord, setEditingRecord] = useState<RechargePackage | null>(null);
  const [form] = Form.useForm();
  const [keyword, setKeyword] = useState('');

  const fetchData = async () => {
    setLoading(true);
    try {
      const params: any = { page, pageSize };
      if (keyword) params.keyword = keyword;
      const res = await api.get('/member-recharge-packages', { params });
      // 拦截器已处理，res.data 就是 { list: [], total: 0 }
      setData(res.data?.list || []);
      setTotal(res.data?.total || 0);
    } catch (error) {
      message.error('获取数据失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [page, pageSize]);

  const handleSearch = () => {
    setPage(1);
    fetchData();
  };

  const handleReset = () => {
    setKeyword('');
    setPage(1);
    fetchData();
  };

  const handleAdd = () => {
    setEditingRecord(null);
    form.resetFields();
    form.setFieldsValue({ sort: 0, isEnabled: 1, validDays: 365 });
    setModalVisible(true);
  };

  const handleEdit = (record: RechargePackage) => {
    setEditingRecord(record);
    form.setFieldsValue(record);
    setModalVisible(true);
  };

  const handleDelete = async (id: string) => {
    try {
      await api.delete(`/member-recharge-packages/${id}`);
      message.success('删除成功');
      fetchData();
    } catch (error) {
      message.error('删除失败');
    }
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      values.totalAmount = (values.amount || 0) + (values.giftAmount || 0);
      if (editingRecord) {
        await api.put(`/member-recharge-packages/${editingRecord.id}`, values);
        message.success('更新成功');
      } else {
        await api.post('/member-recharge-packages', values);
        message.success('创建成功');
      }
      setModalVisible(false);
      fetchData();
    } catch (error) {
      message.error('操作失败');
    }
  };

  const handleToggleEnabled = async (id: string) => {
    try {
      await api.post(`/member-recharge-packages/${id}/toggle`);
      message.success('状态更新成功');
      fetchData();
    } catch (error) {
      message.error('操作失败');
    }
  };

  const columns: ColumnsType<RechargePackage> = [
    { title: '套餐名称', dataIndex: 'name', key: 'name', width: 150 },
    { title: '充值金额', dataIndex: 'amount', key: 'amount', width: 120, render: (v) => `¥${(v / 100).toFixed(2)}` },
    { title: '赠送金额', dataIndex: 'giftAmount', key: 'giftAmount', width: 120, render: (v) => `¥${(v / 100).toFixed(2)}` },
    { title: '到账金额', dataIndex: 'totalAmount', key: 'totalAmount', width: 120, render: (v) => `¥${(v / 100).toFixed(2)}` },
    { title: '有效期(天)', dataIndex: 'validDays', key: 'validDays', width: 100 },
    { title: '排序', dataIndex: 'sort', key: 'sort', width: 80 },
    { 
      title: '状态', 
      dataIndex: 'isEnabled', 
      key: 'isEnabled', 
      width: 100,
      render: (v, record) => (
        <Switch checked={v === 1} onChange={() => handleToggleEnabled(record.id)} checkedChildren="启用" unCheckedChildren="禁用" />
      )
    },
    { title: '创建时间', dataIndex: 'createdAt', key: 'createdAt', width: 180 },
    {
      title: '操作',
      key: 'action',
      width: 150,
      render: (_, record) => (
        <Space>
          <Button type="link" size="small" icon={<EditOutlined />} onClick={() => handleEdit(record)}>编辑</Button>
          <Popconfirm title="确定删除?" onConfirm={() => handleDelete(record.id)}>
            <Button type="link" size="small" danger icon={<DeleteOutlined />}>删除</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Card title="会员充值套餐">
        <Space style={{ marginBottom: 16 }}>
          <Input placeholder="套餐名称" value={keyword} onChange={e => setKeyword(e.target.value)} style={{ width: 200 }} allowClear />
          <Button type="primary" icon={<SearchOutlined />} onClick={handleSearch}>搜索</Button>
          <Button icon={<ReloadOutlined />} onClick={handleReset}>重置</Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>新增套餐</Button>
        </Space>
        <Table
          columns={columns}
          dataSource={data}
          rowKey="id"
          loading={loading}
          pagination={{
            current: page, pageSize, total, showSizeChanger: true, showQuickJumper: true,
            showTotal: (t) => `共 ${t} 条`,
            onChange: (p, ps) => { setPage(p); setPageSize(ps); },
          }}
        />
      </Card>

      <Modal title={editingRecord ? '编辑套餐' : '新增套餐'} open={modalVisible} onOk={handleSubmit} onCancel={() => setModalVisible(false)} width={500}>
        <Form form={form} layout="vertical">
          <Form.Item name="name" label="套餐名称" rules={[{ required: true, message: '请输入套餐名称' }]}>
            <Input placeholder="请输入套餐名称" />
          </Form.Item>
          <Form.Item name="amount" label="充值金额(分)" rules={[{ required: true, message: '请输入充值金额' }]}>
            <InputNumber min={1} style={{ width: '100%' }} placeholder="请输入充值金额，单位：分" />
          </Form.Item>
          <Form.Item name="giftAmount" label="赠送金额(分)">
            <InputNumber min={0} style={{ width: '100%' }} placeholder="请输入赠送金额，单位：分" />
          </Form.Item>
          <Form.Item name="validDays" label="有效期(天)">
            <InputNumber min={1} style={{ width: '100%' }} placeholder="请输入有效期天数" />
          </Form.Item>
          <Form.Item name="description" label="描述">
            <Input.TextArea rows={3} placeholder="请输入描述" />
          </Form.Item>
          <Form.Item name="sort" label="排序">
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default MemberRecharge;
